import {
	Badge,
	IconButton,
	ListItem,
	ListItemText,
	Stack,
	useTheme,
} from "@mui/material";
import { ChevronRight, Mail, MessageCircle, Phone } from "lucide-react";
import type { FC } from "react";
import { AdvisorContactLine } from "web_myplann/Advisor/Components/AdvisorContactLine";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { InitialsAvatar } from "web_myplann/Shared/Components/InitialsAvatar";
import { getPhoneHref } from "web_myplann/Shared/Utils/getPhoneHref";

const R = Resources.Advisor;

type Props = {
	name: string;
	photo?: string;
	phone?: string;
	email: string;
	hasUnread: boolean;
	divider: boolean;
	onOpenChat: () => void;
	onOpenDetail: () => void;
};

export const AdvisorAssistantRow: FC<Props> = ({
	name,
	photo,
	phone,
	email,
	hasUnread,
	divider,
	onOpenChat,
	onOpenDetail,
}) => {
	const theme = useTheme();
	const { t } = useResource();

	return (
		<ListItem
			divider={divider}
			sx={{
				gap: theme.spacingTokens.md,
				px: theme.spacingTokens.lg,
				py: theme.spacingTokens.md,
				borderColor: "surface.border",
			}}
		>
			<InitialsAvatar
				name={name}
				photo={photo}
				size={36}
				muted
				initialsVariant="button"
				flexShrink={0}
			/>
			<ListItemText
				primary={name}
				secondary={
					<Stack alignItems="flex-start">
						{phone && (
							<AdvisorContactLine
								compact
								icon={Phone}
								text={phone}
								href={getPhoneHref(phone)}
								ariaLabel={t(R.phoneAria, { phone })}
							/>
						)}
						<AdvisorContactLine
							compact
							icon={Mail}
							text={email}
							href={`mailto:${email}`}
							ariaLabel={t(R.emailAria, { email })}
						/>
					</Stack>
				}
				slotProps={{
					primary: { noWrap: true },
					secondary: { component: "div" },
				}}
				sx={{ my: 0, minWidth: 0 }}
			/>
			<Stack
				direction="row"
				alignItems="center"
				gap={theme.spacingTokens.lg}
				flexShrink={0}
			>
				<IconButton
					variant="ghost"
					aria-label={t(
						hasUnread ? R.assistantChatUnreadAria : R.assistantChatAria,
						{ name },
					)}
					onClick={onOpenChat}
				>
					<Badge variant="dot" color="error" invisible={!hasUnread}>
						<MessageCircle
							size={theme.iconSize.sm}
							color={theme.palette.blue[500]}
						/>
					</Badge>
				</IconButton>
				<IconButton
					variant="ghost"
					aria-label={t(R.assistantOpenAria, { name })}
					onClick={onOpenDetail}
				>
					<ChevronRight
						size={theme.iconSize.sm}
						color={theme.palette.grey[400]}
					/>
				</IconButton>
			</Stack>
		</ListItem>
	);
};
